/**
 * TextField — Figma component `Input/Text` (instances 12:397, 12:402).
 *
 * Measured: 6px gap between label and field; label `Label/12` in `text/primary`
 * (note: PRIMARY, not muted — the design is explicit); field h-36,
 * `bg/subtle` fill, `border/default` stroke, `radius/sm`, px-12, `Body/14`, with
 * the placeholder in `text/muted`.
 *
 * THE ERROR STATE IS NOT IN FIGMA. `Input/Text` has no invalid variant, and the
 * login screen needs one — a form that refuses input without saying which field
 * is wrong is not shippable. It is authored here from tokens the design already
 * uses for refusal: `status/danger/text` for the message and the same colour at
 * the stroke. That is a documented judgment (recorded in PHASE2-REPORT), and it
 * reuses an existing token pair rather than introducing a colour.
 *
 * `aria-invalid` + `aria-describedby` are wired so the state is not colour-only.
 */

import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** Rendered under the field, in `status/danger/text`, and announced. */
  error?: string | null;
  /** Trailing control inside the field box — the password eye (node 12:555). */
  trailing?: ReactNode;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error = null, trailing, className, ...rest },
  ref,
) {
  const id = useId();
  const errorId = `${id}-error`;
  const invalid = error !== null && error !== '';

  return (
    <div className="flex w-full flex-col gap-1.5">
      <label htmlFor={id} className="text-label font-medium text-fg">
        {label}
      </label>
      <div
        className={cn(
          'flex h-9 w-full items-center gap-2 rounded-sm border bg-subtle px-3',
          invalid ? 'border-danger-fg' : 'border-line',
        )}
      >
        <input
          ref={ref}
          id={id}
          aria-invalid={invalid}
          aria-describedby={invalid ? errorId : undefined}
          className={cn(
            'min-w-0 flex-1 bg-transparent text-body text-fg outline-none',
            'placeholder:text-fg-muted',
            className,
          )}
          {...rest}
        />
        {trailing}
      </div>
      {invalid ? (
        <p id={errorId} className="text-label text-danger-fg">
          {error}
        </p>
      ) : null}
    </div>
  );
});
